import {shuffle} from 'utils/ShuffleArray.js';
import {MOST_POPULAR, RANDOM} from 'constants/sort-by.js';
const DEFAULT_SIZE = 5;

class SearchQuery {
    constructor(store) {
        // filters on the query. each a list of soundIds found by
        // searching ethereum using the selected fields. Used to include
        // sounds in this query
        this.store = store;
        this.selectedTags = [];
        this.selectedOrTags = [];
        this.selectedStyles = [];
        this.selectedCountries = [];
        this.selectedVideos = [];
        this.filterFavorites = false;

        this.chord = false;
        this.year = undefined;
        this.unsortedResults = [];
        this.canceled = false;

        this.mostRecentResults = undefined;
        this.mostPopularResults = undefined;
        this.shuffledResults = undefined;
    }

    get sortBy() {
        return this.store.getState().selector.sortBy;
    }

    // sets the lazy linked list results, so that we can
    // start running the searches, one by one
    async addLazyResults(linkedPromiseList) {
        this.linkedPromiseList = linkedPromiseList;
    }

    // Called one by one while there are still results to be fetched.
    // We take the head of the "linked list of promises" and
    // basically, run that promise. Doing this adds results
    // to the "results" list of this Search Query, aka the actually
    // results that get displayed (see getResults() below)
    async getNext() {
        if (this.linkedPromiseList === undefined || this.canceled) {
            if (!this.canceled) {
                this.setFinalResults(this.unsortedResults);
            }
            return undefined;
        }
        let results = await this.linkedPromiseList.getCurrent();
        results = Array.from(new Set(results));
        results.forEach(result => {
            this.unsortedResults.push(result);
        });
        this.setFinalResults(this.unsortedResults);

        if (this.linkedPromiseList === undefined) {
            return undefined;
        }
        this.linkedPromiseList = await this.linkedPromiseList.getNext();
        return this.linkedPromiseList;
    }

    addUnsortedResults(results) {
        this.unsortedResults = results;
    }

    sort() {
        this.setFinalResults(this.unsortedResults);
    }

    setFinalResults(results) {
        this.unsortedResults = results;
        if (this.sortBy === MOST_POPULAR) {
            if (this.mostPopularResults) {
                this.results = this.mostPopularResults;
            } else {
                this.results = this.sortByRanking([...this.unsortedResults]);
                this.mostPopularResults = this.results;
            }
        } else if (this.sortBy === RANDOM) {
            if (this.shuffledResults === undefined) {
                this.results = shuffle([...this.unsortedResults]);
                this.shuffledResults = this.results;
            } else {
                this.results = this.shuffledResults;
            }
        } else {
            // MOST_RECENT is automatically done in the search anyway
            if (this.mostRecentResults) {
                this.results = this.mostRecentResults;
            } else {
                this.results = this.unsortedResults;
                this.mostRecentResults = this.results;
            }
        }
    }

    sortByRanking(results) {
        if (this.sampleToRanking === undefined) {
            return results;
        }
        results.sort((a, b) => {
            let rankingA = a in this.sampleToRanking ? this.sampleToRanking[a] : 0;
            let rankingB = b in this.sampleToRanking ? this.sampleToRanking[b] : 0;

            return rankingB - rankingA;
        });

        return results;
    }

    getAllSounds() {
        let results = this.favorites === undefined ||
            this.results === undefined ?
            this.results :
            this.results.filter(
                result => this.favorites.indexOf(result) > -1);

        return Array.from(new Set(results));
    }

    // returns the results to be shown, directly to the front end
    getResults(start=undefined, size=DEFAULT_SIZE) {
        let results = this.getAllSounds();

        //results = this.sortByRanking(results);

        //results = shuffle(results);

        if (results !== undefined) {
            if (this.stylesSounds !== undefined) {
                results = results.filter(
                    sound =>
                        this.stylesSounds.has(sound));
            }
            if (this.yearSounds !== undefined) {
                results = results.filter(
                    sound =>
                        this.yearSounds.has(sound));
            }
            if (this.chordSounds !== undefined) {
                results = results.filter(
                    sound =>
                        this.chordSounds.has(sound));
            }
            if (this.videosSounds !== undefined) {
                results = results.filter(
                    sound =>
                        this.videosSounds.has(sound));
            }
        }

        if (results === undefined) {
            return {sounds: [], total: 0};
        }

        if (start !== undefined && size !== undefined) {
            if (start >= results.length) {
                return {sounds: [], total: 0};
            }

            if (start + size >= results.length) {
                let hashes = results.slice(start);
                return {
                    sounds: hashes,
                    total: results.length
                };
            } else {
                let hashes = results.slice(start, start + size);
                return {
                    sounds: hashes,
                    total: results.length
                };
            }
        }

        return {
            sounds: results,
            total: results.length
        };
    }

    cancel() {
        this.canceled = true;
    }

    empty() {
        return this.selectedTags.length === 0 &&
            this.selectedStyles.length === 0 &&
            this.selectedOrTags.length === 0 &&
            this.selectedCountries.length ==- 0 &&
            this.selectedVideos.length === 0 &&
            this.chord === undefined &&
            this.year === undefined;
    }

    equals(other) {
        let chordsEqual = false;
        if (this.chord === other.chord) {
            chordsEqual = true;
        } else if (this.chord === undefined ||
                  other.chord === undefined) {
            chordsEqual = false;
        } else if (this.chord.join(',') === other.chord.join(',')) {
            chordsEqual = true; 
        }

        return eq(this.selectedTags, other.selectedTags) &&
            eq(this.selectedOrTags, other.selectedOrTags) &&
            eq(this.selectedStyles, other.selectedStyles) &&
            eq(this.selectedVideos , other.selectedVideos) &&
            eq(this.selectedDiscogs, other.selectedDiscogs) &&
            eq(this.selectedCountries, other.selectedCountries) &&
            dateEq(this.fromDate, other.fromDate) &&
            this.filterFavorites === other.filterFavorites &&
            chordsEqual &&
            this.year === other.year;
    }
}

function dateEq(a, b) {
    if (a !== undefined && b === undefined) {
        return false;
    }
    if (b !== undefined && a === undefined) {
        return false;
    }
    if (a === undefined && b === undefined) {
        return true;
    }

    a = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    b = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    let ms = Math.abs(a - b)
    let diff = Math.floor(ms/1000/60/60/24); //floor should be unnecessary, but just in case
    if (diff === 0) {
        return true;
    }

    return false;
}

function eq(a, b) {
    let A = new Set(a);
    let B = new Set(b);

    return Array.from(A).every(v => B.has(v)) &&
        Array.from(B).every(v => A.has(v));
}


export default SearchQuery;
